import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ComprehensiveErrorBoundary } from "@/components/error/ComprehensiveErrorBoundary";
import { BlankPageDetector } from "@/components/error/BlankPageDetector";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { MainLayout } from "./components/layout/MainLayout";
import { LazyComponentWrapper } from "./components/ui/loading/LazyComponentWrapper";
import { performanceMonitor } from "./utils/performance";
import { supabase } from "@/integrations/supabase/client";

// Core synchronous imports (always loaded)
import Dashboard from "./pages/Dashboard";
import Assessment from "./pages/Assessment";
import AssessmentPopup from "./pages/AssessmentPopup";
import Register from "./pages/Register";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import { ColorSystemTest } from "./components/test/ColorSystemTest";
import { logger } from "./utils/logger";
import { RouteErrorBoundary } from "@/components/error/RouteErrorBoundary";

// Lazy loaded route bundles
// Admin Bundle
const AdminPage = lazy(() => import("./pages/admin/AdminPage"));
const Status = lazy(() => import("./pages/Status"));
const AuditLogPage = lazy(() => import("./pages/admin/AuditLogPage"));
const AssessmentExportPage = lazy(() => import("./pages/admin/AssessmentExportPage"));
const AdminResetPage = lazy(() => import("./pages/admin/AdminResetPage"));
const ConsultantsPage = lazy(() => import("./pages/consultant/ConsultantsPage").then(m => ({ default: m.ConsultantsPage })));
const ClientProfile = lazy(() => import("./pages/consultant/ClientProfile"));
const ClientsPage = lazy(() => import("./pages/clients/ClientsPage"));

// Platform Admin
import { PlatformAdminDashboard } from "./pages/PlatformAdminDashboard";
import { PlatformAdminRoute } from "./components/platform-admin/PlatformAdminRoute";

// Business Bundle - Lazy Loaded for Performance (~200KB)
const Company = lazy(() => import("./pages/Company"));
const Valuations = lazy(() => import("./pages/Valuations"));
const Benchmarks = lazy(() => import("./pages/Benchmarks"));
const Plan = lazy(() => import("./pages/Plan"));
const SuccessorsPage = lazy(() => import("./pages/successors/SuccessorsPage"));

// Secondary Bundle - Lazy Loaded for Performance (~180KB)
const Settings = lazy(() => import("./pages/Settings"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const Team = lazy(() => import("./pages/Team"));
const Learn = lazy(() => import("./pages/Learn"));
const Documents = lazy(() => import("./pages/Documents"));
const Tasks = lazy(() => import("./pages/Tasks"));
const ReportsPage = lazy(() => import("./pages/reports/ReportsPage"));
const NotificationsPage = lazy(() => import("./pages/notifications/NotificationsPage"));
import { TooltipProvider } from "@/components/ui/tooltip";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { NavigationProvider } from "@/contexts/NavigationContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UserPreferencesProvider } from "@/contexts/UserPreferencesContext";
import { ProxyProvider } from "@/contexts/ProxyContext";
import { InactivityMonitor } from "@/components/auth/InactivityMonitor";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { ThemeProvider } from "next-themes";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import VerifyEmail from "./pages/auth/VerifyEmail";
import CompleteProfile from "./pages/auth/CompleteProfile";
import { useThemeSync } from "./hooks/useThemeSync";
import { OfflineIndicator } from "@/components/ui/offline-indicator";
import { PWAInstallPrompt } from "@/components/ui/pwa-install-prompt";
import { usePWA } from "@/hooks/usePWA";
import { useState } from "react";
import { GlobalErrorBoundary } from "@/components/error/GlobalErrorBoundary";
import { useHealthCheck } from "@/hooks/useHealthCheck";
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      retryOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});

const ThemeSyncWrapper = ({ children }: { children: React.ReactNode }) => {
  useThemeSync(); // Load user's saved theme on auth
  return <>{children}</>;
};

const PWAWrapper = ({ children }: { children: React.ReactNode }) => {
  const { canInstall, isStandalone } = usePWA();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  // Show install prompt after some delay for better UX
  useEffect(() => {
    if (canInstall && !isStandalone) {
      const timer = setTimeout(() => {
        setShowInstallPrompt(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [canInstall, isStandalone]);

  return (
    <>
      <OfflineIndicator />
      {showInstallPrompt && !isStandalone && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <PWAInstallPrompt 
            variant="card"
            onDismiss={() => setShowInstallPrompt(false)} 
          />
        </div>
      )}
      {children}
    </>
  );
};

// Ensures AuthProvider finished initialization before rendering routes
// Prevents race condition where lazy components access Auth Context during getSession()
const AuthInitializedRoutes = () => {
  const { loading } = useAuth();
  
  // Show branded spinner while auth initializes on cold start
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="h-16 w-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
          </div>
          <p className="text-sm text-muted-foreground">Initializing...</p>
        </div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public routes - these need to be inside AuthProvider */}
      <Route path="/" element={
        <BlankPageDetector>
          <ComprehensiveErrorBoundary level="page" enableRetry={true}>
            <Login />
          </ComprehensiveErrorBoundary>
        </BlankPageDetector>
      } />
      <Route path="/login" element={
        <BlankPageDetector>
          <ComprehensiveErrorBoundary level="page" enableRetry={true}>
            <Login />
          </ComprehensiveErrorBoundary>
        </BlankPageDetector>
      } />
      <Route path="/register" element={
        <BlankPageDetector>
          <ComprehensiveErrorBoundary level="page" enableRetry={true}>
            <Register />
          </ComprehensiveErrorBoundary>
        </BlankPageDetector>
      } />
      
      {/* Auth routes */}
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/auth/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/complete-profile" element={<CompleteProfile />} />
      
      {/* Public legal pages */}
      <Route path="/terms" element={<LazyComponentWrapper routeName="terms"><Terms /></LazyComponentWrapper>} />
      <Route path="/privacy" element={<LazyComponentWrapper routeName="privacy"><Privacy /></LazyComponentWrapper>} />

      {/* Public status page */}
      <Route path="/status" element={
        <LazyComponentWrapper routeName="status">
          <Status />
        </LazyComponentWrapper>
      } />
      
      {/* Protected routes with layout */}
      <Route element={<AuthGuard><MainLayout /></AuthGuard>}>
        {/* Core synchronous routes */}
        <Route path="/dashboard" element={
          <RouteErrorBoundary routeName="dashboard" preserveProgress={false}>
            <Dashboard />
          </RouteErrorBoundary>
        } />
        <Route path="/assessment" element={
          <RouteErrorBoundary routeName="assessment" preserveProgress={true}>
            <Assessment />
          </RouteErrorBoundary>
        } />
        
        {/* Development/Test Routes */}
        <Route path="/color-test" element={<ColorSystemTest />} />
        
        {/* Platform Admin Dashboard */}
        <Route path="/admin" element={
          <PlatformAdminRoute>
            <PlatformAdminDashboard />
          </PlatformAdminRoute>
        } />
        
        <Route path="/admin/audit-log" element={
          <RouteErrorBoundary routeName="audit-log" preserveProgress={false}>
            <LazyComponentWrapper routeName="audit-log">
              <AuditLogPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/admin/assessment-export" element={
          <RouteErrorBoundary routeName="assessment-export" preserveProgress={false}>
            <LazyComponentWrapper routeName="assessment-export">
              <AssessmentExportPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/admin/reset" element={
          <RouteErrorBoundary routeName="admin-reset" preserveProgress={false}>
            <LazyComponentWrapper routeName="admin-reset">
              <AdminResetPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/consultants" element={
          <RouteErrorBoundary routeName="consultants" preserveProgress={false}>
            <LazyComponentWrapper routeName="consultants">
              <ConsultantsPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/clients" element={
          <RouteErrorBoundary routeName="clients" preserveProgress={false}>
            <LazyComponentWrapper routeName="clients">
              <ClientsPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/consultants/clients/:clientId" element={
          <RouteErrorBoundary routeName="client-profile" preserveProgress={false}>
            <LazyComponentWrapper routeName="client-profile">
              <ClientProfile />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        
        {/* Business Bundle - Lazy loaded */}
        <Route path="/company" element={
          <RouteErrorBoundary routeName="company" preserveProgress={true}>
            <LazyComponentWrapper routeName="company">
              <Company />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/valuations" element={
          <RouteErrorBoundary routeName="valuations" preserveProgress={false}>
            <LazyComponentWrapper routeName="valuations">
              <Valuations />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/benchmarks" element={
          <RouteErrorBoundary routeName="benchmarks" preserveProgress={false}>
            <LazyComponentWrapper routeName="benchmarks">
              <Benchmarks />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/plan" element={
          <RouteErrorBoundary routeName="plan" preserveProgress={true}>
            <LazyComponentWrapper routeName="plan">
              <Plan />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/successors" element={
          <RouteErrorBoundary routeName="successors" preserveProgress={true}>
            <LazyComponentWrapper routeName="successors">
              <SuccessorsPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        
        {/* Secondary Bundle - Lazy loaded */}
        <Route path="/settings" element={
          <RouteErrorBoundary routeName="settings" preserveProgress={true}>
            <LazyComponentWrapper routeName="settings">
              <Settings />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/profile" element={
          <RouteErrorBoundary routeName="profile" preserveProgress={true}>
            <LazyComponentWrapper routeName="profile">
              <ProfilePage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/team" element={
          <RouteErrorBoundary routeName="team" preserveProgress={false}>
            <LazyComponentWrapper routeName="team">
              <Team />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/learn" element={
          <RouteErrorBoundary routeName="learn" preserveProgress={false}>
            <LazyComponentWrapper routeName="learn">
              <Learn />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/documents" element={
          <RouteErrorBoundary routeName="documents" preserveProgress={false}>
            <LazyComponentWrapper routeName="documents">
              <Documents />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/tasks" element={
          <RouteErrorBoundary routeName="tasks" preserveProgress={false}>
            <LazyComponentWrapper routeName="tasks">
              <Tasks />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/reports" element={
          <RouteErrorBoundary routeName="reports" preserveProgress={false}>
            <LazyComponentWrapper routeName="reports">
              <ReportsPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
        <Route path="/notifications" element={
          <RouteErrorBoundary routeName="notifications" preserveProgress={false}>
            <LazyComponentWrapper routeName="notifications">
              <NotificationsPage />
            </LazyComponentWrapper>
          </RouteErrorBoundary>
        } />
      </Route>
      
      {/* Catch-all route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

/**
 * Health check wrapper — only runs for platform admins, at 5-minute intervals.
 * Must be rendered inside AuthProvider to access useAuth.
 */
const HealthCheckWrapper = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setIsPlatformAdmin(false);
      return;
    }
    // Quick check — only fetch the admin flag, not the full profile
    supabase
      .from('profiles')
      .select('is_platform_admin')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setIsPlatformAdmin(data?.is_platform_admin === true);
      });
  }, [user?.id]);

  const { isHealthy } = useHealthCheck({
    interval: 300000, // 5 minutes (was 60s)
    failureThreshold: 3,
    enabled: isPlatformAdmin, // Only active for platform admins
  });

  return (
    <>
      {!isHealthy && isPlatformAdmin && (
        <div className="bg-destructive/10 text-destructive border-b border-destructive/20 p-3 text-center text-sm">
          ⚠️ System experiencing connectivity issues. Some features may be unavailable.
        </div>
      )}
      {children}
    </>
  );
};

const App = () => {

  useEffect(() => {
    // Initialize performance monitoring
    performanceMonitor.init();
    
    // Register service worker for offline support
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .then(registration => {
            logger.info('Service worker registered', { scope: registration.scope });
          })
          .catch(error => {
            logger.error('Service worker registration failed', error);
          });
      });
    }
  }, []);

  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
            <TooltipProvider>
              <AuthProvider>
                <UserPreferencesProvider>
                  <ProxyProvider>
                    <ThemeSyncWrapper>
                      <NavigationProvider>
                        <ProfileProvider>
                          <PWAWrapper>
                            <HealthCheckWrapper>
                              <InactivityMonitor />
                              <Toaster />
                              <Sonner />
                              <AuthInitializedRoutes />
                            </HealthCheckWrapper>
                          </PWAWrapper>
                        </ProfileProvider>
                      </NavigationProvider>
                    </ThemeSyncWrapper>
                  </ProxyProvider>
                </UserPreferencesProvider>
              </AuthProvider>
            </TooltipProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
};

export default App;
